<mat-expansion-panel [disabled]="!addressReady" [expanded]="!userControlledAccordion && addressReady && !shippingReady" (opened)="onAccordionToggle()" (closed)="onAccordionToggle()">
  <mat-expansion-panel-header>
    <mat-panel-title>
      <mat-icon [ngClass]="shippingSectionClass()">{{shippingSectionIcon()}}</mat-icon>
      <span i18n="outbound delivery|Delivery of the order to the customer. Not Billing, and not the freight cost line.@@Shipping">Shipping</span>
    </mat-panel-title>
    <!-- Show the current selection in the header whenever the cart
         has one, even before the user has explicitly confirmed it by
         clicking a radio (i.e. while `shippingReady` is still false
         because we're showing an auto-selected default). Previously
         this was gated on `shippingReady`, which meant the header
         permanently read "Select a shipping option" even after the
         auto-select seeded the cheapest option — users had no way to
         tell what was currently selected without scanning the list
         (bug 2026-07-09 pass 2). The section-hint variants below
         only fire when there's no selection to display.

         Header cost = shipping + packing combined (per 2026-07-09
         pass 3). `cart.shippingCost` alone is now the pure shipping
         charge (split from packing on the cart so the order summary
         can list them separately for tax jurisdictions that treat
         them differently — see `Cart.setSelectedShippingChoice`).
         The picker still combines them here because the "which
         shipping option is selected" decision hinges on the total
         out-of-pocket charge, not the split. -->
    <mat-panel-description *ngIf="cart.selectedOrderOption">
      <span>{{cart.selectedCarrier}}&nbsp;{{cart.selectedServiceLevel}}</span>
      &nbsp;— ${{(cart.shippingCost + cart.packingAndPackagingCost) | number: '1.2-2'}}
      <span *ngIf="!shippingReady" class="section-hint-inline" i18n="@@tentative">(tentative)</span>
    </mat-panel-description>
    <mat-panel-description *ngIf="!addressReady" class="section-hint">
      <span i18n="@@Enter delivery address first">Enter delivery address first</span>
    </mat-panel-description>
    <mat-panel-description *ngIf="addressReady && !cart.selectedOrderOption && isShippingComputing" class="section-hint">
      <span i18n="@@Getting shipping options">Getting shipping options</span>
    </mat-panel-description>
    <mat-panel-description *ngIf="addressReady && !cart.selectedOrderOption && !isShippingComputing" class="section-hint">
      <span i18n="@@Select a shipping option">Select a shipping option</span>
    </mat-panel-description>
  </mat-expansion-panel-header>

  <div class="errorMessage" *ngIf="cart.shippingErrorMessage.length > 0"><h3 color="warn">{{cart.shippingErrorMessage}}</h3></div>
  <ng-container *ngIf="!cart.selectedOrderOption && cart">
    <div class="errorMessage" *ngIf="cart.shippingStatus==='INITIAL'" i18n="@@No shipping options available">No shipping options available</div>
    <div class="errorMessage" *ngIf="cart.shippingStatus==='ERROR'" i18n="@@Error computing shipping options">Error computing shipping options</div>
    <div class="checkout-computing" *ngIf="isShippingComputing">
      <mat-progress-spinner color="primary" mode="indeterminate" diameter="20"></mat-progress-spinner>
      <span i18n="@@Getting shipping options">Getting shipping options</span>
    </div>
    <!-- All packages have finished computing but the model returned no
         unified choices — surface a clear "no options" message rather
         than leaving the section blank. Never seen in normal cases;
         safety net for a backend that returns packages but no
         per-package rate lines (e.g. carrier account misconfigured,
         address unroutable). -->
    <div class="errorMessage"
         *ngIf="cart.shippingStatus === 'READY' && !isShippingComputing && unifiedShippingChoices.length === 0"
         i18n="@@No shipping options available">No shipping options available</div>
  </ng-container>
  <!-- (removed 2026-07-23) The manual "Compute order costs" button was
       removed. `cart.compute()` now fires automatically from
       `CartPageComponent.maybeAutoComputeShipping()` whenever the
       address settles or CHECKOUT is (re-)entered, and from
       `scheduleShippingRecompute()` (debounced) on every item quantity
       change, so the manual trigger is redundant. -->

  <!-- Unified per-(carrier, serviceLevel) shipping picker.
       Each row is a distinct carrier + service offering; the backing
       package layout is chosen by the model to minimise cost for that
       specific (carrier, service). The flat list is cost-sorted
       ascending by `Cart.getUnifiedShippingChoices()`.

       Layout: two filter chip rows (carrier + transit) built from
       whatever ranges/carriers the data actually contains, followed
       by a "best only ↔ show all" toggle and the radio group. Each
       row shows a coloured carrier pill, a coloured transit pill
       (traffic-light on `deliveryDaysMax`), a green check-mark for
       per-carrier Pareto-optimal choices, plus the existing
       "Recommended" (org preferred) / "Best price" (global cheapest)
       badges. TOTAL cost dominates the row visually (large, right-
       aligned); shipping+packing breakdown, box summary, and
       facility line are demoted underneath. -->
  <div *ngIf="unifiedShippingChoices.length > 0" class="unified-shipping-choices">
    <!-- Filter chip rows. Built from the current data — not fixed
         sets — so chips appear/disappear as the server's rate
         responses land or the item set changes. Clicking a chip
         toggles it in/out of the corresponding filter set.

         Layout (2026-07-09 pass 4): the three filter groups
         (Carrier, Transit time, Show best/all toggle) sit on a
         single horizontal `flex-wrap` row when the panel is wide
         enough, and gracefully break onto separate lines only
         when the combined width would overflow. Each group is a
         `flex-nowrap` cluster so its label + chips stay together
         when the outer row wraps — carrier chips never orphan
         from the "Carrier" label, etc. -->
    <div class="unified-shipping-filters">
      <div class="unified-shipping-filter-group" *ngIf="availableCarriers.length > 1">
        <span class="unified-shipping-filter-label" i18n="@@Carrier">Carrier</span>
        <button type="button"
                *ngFor="let carrier of availableCarriers; trackBy: trackCarrier"
                class="unified-shipping-chip carrier-chip"
                [class.unified-shipping-chip-active]="isCarrierFilterActive(carrier)"
                [style.backgroundColor]="isCarrierFilterActive(carrier) ? getCarrierColor(carrier).bg : ''"
                [style.borderColor]="getCarrierColor(carrier).border"
                [style.color]="getCarrierColor(carrier).fg"
                (click)="toggleCarrierFilter(carrier)">
          <span class="carrier-swatch" [style.backgroundColor]="getCarrierColor(carrier).fg"></span>
          {{carrier}}
        </button>
        <button type="button"
                *ngIf="carrierFilter.size > 0"
                class="unified-shipping-chip unified-shipping-chip-clear"
                (click)="clearCarrierFilter()"
                i18n="@@All">All</button>
      </div>
      <div class="unified-shipping-filter-group" *ngIf="availableTransitBuckets.length > 1">
        <span class="unified-shipping-filter-label" i18n="@@Transit time">Transit time</span>
        <button type="button"
                *ngFor="let bucket of availableTransitBuckets; trackBy: trackTransit"
                class="unified-shipping-chip transit-chip"
                [class.unified-shipping-chip-active]="isTransitFilterActive(bucket)"
                (click)="toggleTransitFilter(bucket)">
          {{bucket.label}}
        </button>
        <button type="button"
                *ngIf="transitFilter.size > 0"
                class="unified-shipping-chip unified-shipping-chip-clear"
                (click)="clearTransitFilter()"
                i18n="@@All">All</button>
      </div>
      <div class="unified-shipping-filter-group unified-shipping-toggle-group">
        <button type="button" class="unified-shipping-toggle" (click)="toggleShowAll()">
          <mat-icon>{{showAllOptions ? 'star' : 'unfold_more'}}</mat-icon>
          <span *ngIf="!showAllOptions" i18n="@@Show all options">Show all options</span>
          <span *ngIf="showAllOptions" i18n="@@Show best options">Show best options</span>
          <span *ngIf="!showAllOptions && hiddenByBestOnlyCount > 0" class="unified-shipping-toggle-hint">
            (+{{hiddenByBestOnlyCount}})
          </span>
        </button>
      </div>
    </div>

    <div class="unified-shipping-empty" *ngIf="visibleShippingChoices.length === 0"
         i18n="@@No options match your filters">No options match your filters</div>

    <mat-radio-group class="radio-group" *ngIf="visibleShippingChoices.length > 0"
                     [value]="selectedChoiceKey">
      <mat-radio-button *ngFor="let choice of visibleShippingChoices; trackBy: trackChoice"
                        [value]="choiceKeyOfPublic(choice)"
                        [checked]="isChoiceSelected(choice)"
                        (change)="onChoiceSelected(choice)"
                        class="unified-shipping-choice"
                        [class.unified-shipping-choice-selected]="isChoiceSelected(choice)"
                        [class.unified-shipping-choice-best-carrier]="isBestForCarrier(choice)">
        <div class="unified-shipping-choice-body">
          <div class="unified-shipping-choice-main">
            <div class="unified-shipping-choice-title">
              <mat-icon *ngIf="isBestForCarrier(choice)"
                        class="unified-shipping-choice-check"
                        matTooltip="Best value for this carrier"
                        i18n-matTooltip="@@Best value for this carrier">check_circle</mat-icon>
              <span class="unified-shipping-choice-carrier-pill"
                    [style.backgroundColor]="getCarrierColor(choice.carrier).bg"
                    [style.borderColor]="getCarrierColor(choice.carrier).border"
                    [style.color]="getCarrierColor(choice.carrier).fg">
                {{choice.carrier}}
              </span>
              <span class="unified-shipping-choice-service">{{choice.serviceLevel}}</span>
              <span *ngIf="hasDeliveryEstimate(choice)"
                    class="unified-shipping-choice-transit-pill"
                    [style.backgroundColor]="getTransitColor(choice).bg"
                    [style.borderColor]="getTransitColor(choice).border"
                    [style.color]="getTransitColor(choice).fg">
                <mat-icon class="unified-shipping-choice-transit-icon">schedule</mat-icon>
                {{formatDeliveryDaysRange(choice)}}
                <ng-container *ngIf="choice.deliveryDaysMax === 1" i18n="@@day">day</ng-container>
                <ng-container *ngIf="choice.deliveryDaysMax !== 1" i18n="@@days">days</ng-container>
              </span>
              <span *ngIf="isPreferredChoice(choice)" class="unified-shipping-choice-badge badge-preferred"
                    i18n="@@OrganizationDefaultBadge">Organization default</span>
              <span *ngIf="!isPreferredChoice(choice) && isBestPriceChoice(choice)"
                    class="unified-shipping-choice-badge badge-best"
                    i18n="@@Best price">Best price</span>
            </div>
            <div class="unified-shipping-choice-detail">
              <span *ngIf="choice.packingCost > 0">
                <ng-container i18n="@@shipping">shipping</ng-container>
                ${{choice.shippingCost | number: '1.2-2'}}
                <span class="detail-sep">+</span>
                <ng-container i18n="@@packing">packing</ng-container>
                ${{choice.packingCost | number: '1.2-2'}}
              </span>
              <span *ngIf="choice.packageSummary" class="unified-shipping-choice-packages">
                <span class="detail-sep" *ngIf="choice.packingCost > 0">·</span>
                {{choice.packageSummary}}
              </span>
            </div>
            <div *ngFor="let pnoo of choice.orderOption.printNodeOrderOptions" class="unified-shipping-choice-facility">
              <span i18n="@@from">from</span>
              {{facilityFriendlyNames['' + pnoo.facilityId] || ('Facility ' + pnoo.facilityId)}}
            </div>
          </div>
          <div class="unified-shipping-choice-total">${{choice.totalCost | number: '1.2-2'}}</div>
        </div>
      </mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- "Set as organization default" affordance.
       Offered when the currently-selected shipping option differs
       from the organization's stored `preferredCarrier` /
       `preferredServiceLevel` AND the user is currently acting in
       the `organization` role (same permission gate the
       organization settings page uses). Click PUTs the updated
       organization record; on success the button hides itself
       because the selection now matches the freshly-saved
       preference. Small inline states cover in-flight save,
       success confirmation, and PUT failure. -->
  <div class="set-org-default-row" *ngIf="canSetOrgPreferredShipping || setDefaultState === 'SAVING' || setDefaultState === 'SAVED' || setDefaultState === 'ERROR'">
    <button type="button"
            *ngIf="setDefaultState === 'IDLE' && canSetOrgPreferredShipping"
            class="set-org-default-btn"
            (click)="setOrgPreferredShipping()">
      <mat-icon>push_pin</mat-icon>
      <!-- Label is split into three pieces so each translatable
           fragment is a plain static string with no interpolations
           or placeholders (simpler for the .xtb pipeline and avoids
           any placeholder-ordering pitfalls). The bolded
           carrier/service value lives in its own non-i18n span
           between the two static fragments. Word order across the
           three siblings is not translator-reorderable — accept
           that trade-off in exchange for placeholder-free strings. -->
      <span i18n="@@Set">Set</span>
      <strong class="set-org-default-selection">{{cart.selectedCarrier}}&nbsp;{{cart.selectedServiceLevel}}</strong>
      <span i18n="@@as the default carrier and service level">as the default carrier and service level</span>
      <span class="set-org-default-detail">
        <span i18n="@@currently">currently</span>
        <ng-container *ngIf="cart.organization.preferredCarrier || cart.organization.preferredServiceLevel; else noCurrentPreference">
          <strong>{{cart.organization.preferredCarrier}}&nbsp;{{cart.organization.preferredServiceLevel}}</strong>
        </ng-container>
        <ng-template #noCurrentPreference>
          <em i18n="@@none set">none set</em>
        </ng-template>
      </span>
    </button>
    <span *ngIf="setDefaultState === 'SAVING'" class="set-org-default-status">
      <mat-progress-spinner color="primary" mode="indeterminate" diameter="16"></mat-progress-spinner>
      <span i18n="@@Saving">Saving</span>
    </span>
    <span *ngIf="setDefaultState === 'SAVED'" class="set-org-default-status set-org-default-status-saved">
      <mat-icon>check_circle</mat-icon>
      <span i18n="@@Saved as organization default">Saved as organization default</span>
    </span>
    <span *ngIf="setDefaultState === 'ERROR'" class="set-org-default-status set-org-default-status-error">
      <mat-icon>error_outline</mat-icon>
      <span>{{setDefaultErrorMessage}}</span>
      <button type="button" class="set-org-default-retry" (click)="setOrgPreferredShipping()" i18n="@@Retry">Retry</button>
    </span>
  </div>

  <!-- The trailing "selected order option" summary block (carrier +
       service + facility + item list) previously lived here. It was
       redundant with the picker rows themselves, which already show
       the carrier, service, facility name, and cost per row. The
       item breakdown (quantity × title) is redundant with the cart's
       items panel shown above. Removed 2026-07-09 per UX pass 2. -->
</mat-expansion-panel>










